<template>
    <div ref="widgetRef" v-show="seating.WidgetStatus !== GordianWidgetStatus.ERROR" />
</template>

<script lang="ts">
    import { defineComponent, onMounted, PropType, ref } from 'vue';
    import {
        GordianSeating,
        GordianWidgetStatus,
        SeatingProduct,
    } from '@/api/Cart/cart.interfaces';
    import { theme } from './Theme';
    import { t } from '@shared/helpers/translation';
    import { GordianResponse } from '../Gordian.interfaces';
    import { formatGordianBasketResponse } from '@/helpers/seating';
    import { useHead } from '@unhead/vue';
    import { suppressClarityData } from '@/tracking/clarity/Helpers/suppress';

    declare global {
        interface Window {
            Gordian: any;
        }
    }

    const WIDGET_TIMEOUT_IN_SECONDS = 20;
    const updateInterVal = 200;
    const defaultUpdateCounter = WIDGET_TIMEOUT_IN_SECONDS / (updateInterVal / 1000);

    export default defineComponent({
        name: 'GordianWidget',
        computed: {
            GordianWidgetStatus() {
                return GordianWidgetStatus;
            },
        },
        props: {
            seating: {
                type: Object as PropType<GordianSeating>,
                required: true,
            },
            showBookingFee: {
                type: Boolean,
                required: true,
            },
            flightDescription: {
                type: String,
            },
            display: {
                type: String as PropType<'modal' | 'embedded'>,
                default: 'embedded',
            },
            onSeatsChanged: {
                type: Function as PropType<(seats: SeatingProduct[]) => void>,
                required: true,
            },
            onCreate: {
                type: Function as PropType<() => void>,
                required: true,
            },
            onCreateComplete: {
                type: Function as PropType<() => void>,
                required: true,
            },
            onError: {
                type: Function as PropType<(error: any) => void>,
                required: true,
            },
            onModalClose: {
                type: Function,
            },
        },
        methods: {
            t,
        },
        setup(props) {
            const { display, seating } = props;
            const tripId = seating.TripId ?? '';
            const searchId = seating.SearchId ?? '';
            const tripAccessToken = seating.TripAccessToken ?? '';
            const widgetRef = ref<Element>();
            const externalScriptIsLoaded = !!window.Gordian;
            const onError = props.onError;

            useHead({
                script: externalScriptIsLoaded
                    ? []
                    : [
                          {
                              src: 'https://sdk.gordiansoftware.com/javascript/v2.2/gordian.min.js',
                              async: true,
                              onload: () => createWidget(),
                          },
                      ],
            });

            onMounted(() => {
                props.onCreate();
                if (externalScriptIsLoaded) {
                    createWidget();
                }
            });

            const onModalClose = (modalCallback: Function) => {
                const el = widgetRef.value;
                const closeButton = el?.querySelector('#modal-close-button');
                const gordianOverlay = el?.querySelector('#gordian-overlay');
                closeButton?.addEventListener('click', () => modalCallback());

                gordianOverlay?.addEventListener('click', (e: Event) => {
                    const overlay: HTMLDivElement = e.target as HTMLDivElement;
                    if (overlay?.classList?.contains('gordian-overlay')) {
                        modalCallback();
                    }
                });
            };

            const updateWidgetLoading = (counter = defaultUpdateCounter) => {
                setTimeout(() => {
                    const widgetIsLoaded = !!widgetRef.value?.querySelector(
                        '#gordian-upsell-container:last-child .gordian-seatmap'
                    );
                    if (!widgetIsLoaded && counter > 0) {
                        updateWidgetLoading(counter - 1);
                    } else if (widgetIsLoaded) {
                        props.onCreateComplete();

                        suppressClarityData([
                            '#passenger-select-select',
                            '[id^="compartment-main"]',
                            '#passenger-select',
                            '#seat-details-container',
                        ]);

                        if (props.onModalClose) {
                            onModalClose(props.onModalClose);
                        }
                    } else {
                        onError(
                            `Gordian widget create - timeout after ${WIDGET_TIMEOUT_IN_SECONDS} seconds`
                        );
                    }
                }, updateInterVal);
            };

            const createWidget = () => {
                window.Gordian.init({
                    searchIds: { seat: searchId },
                    tripId,
                    tripAccessToken,
                    onBasketChange: (gordianResponse: GordianResponse) => {
                        const seats = formatGordianBasketResponse(gordianResponse);
                        if (props.onSeatsChanged) {
                            props.onSeatsChanged(seats);
                        }
                    },
                });
                window.Gordian.showUpsell({
                    container: widgetRef.value,
                    display,
                    allowProducts: ['seats'],
                    theme,
                    onError,
                    ...(props.showBookingFee && {
                        variants: {
                            convenience_fee_variant: 'variant',
                            convenience_fee_text: 'booking fee',
                        },
                    }),
                })
                    .catch((error: any) => {
                        onError(`Gordian Upsell - unable to create: ${error.message}`);
                    })
                    .then(updateWidgetLoading);
            };

            return {
                widgetRef,
            };
        },
    });
</script>
<style lang="scss">
    @forward 'Widget';
</style>
